/**
 * City + state combined input (input_subtype: city_state) — Alpine component.
 * Moved from the inline script in components/city-state-combined-input.blade.php:
 * inline blade scripts die when the component first appears via a Livewire
 * morph (morphdom doesn't execute injected script tags), which left the whole
 * field dead — no autocomplete AND no way to save manual input.
 *
 * Manual entry binds unconditionally in init(); Google Places is a progressive
 * enhancement layered on top when the central loader resolves. Every keystroke
 * persists the typed text (as the same {city, state, formatted} JSON the manual
 * 'change' path always produced) so a typed value survives Continue even if
 * the user never blurs or selects a suggestion — with or without Google.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';

export default function cityStateCombinedInput(modelValue) {
    return {
        autocomplete: null,
        placeSelected: false,
        modelValue: modelValue,

        init() {
            this.bindManualEntry();

            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            // Set initial value if exists
            this.parseExistingValue();

            this.$watch('modelValue', (value) => {
                this.parseExistingValue();
            });
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            const input = this.$refs.cityInput;
            if (!input) return;

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Continue without change/blur/selection. Typing also
            // clears the selection flag: latest manual edit wins again.
            input.addEventListener('input', () => {
                this.placeSelected = false;
                this.commitManual(input.value);
            });

            // Handle manual input (user typing without selecting from autocomplete)
            input.addEventListener('change', () => {
                this.commitManual(input.value);
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // placeSelected before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.placeSelected && input.value.trim()) {
                        this.commitManual(input.value);
                    }
                }, 250);
            });
        },

        /**
         * Manual save path — body moved verbatim out of initAutocomplete()'s
         * 'change' listener so it works without Google.
         */
        commitManual(rawValue) {
            const value = rawValue.trim();
            if (value) {
                // Try to parse as "City, State" format
                const parts = value.split(',').map(p => p.trim());
                let city = '', state = '';

                if (parts.length >= 2) {
                    city = parts[0];
                    state = parts[1];
                } else {
                    city = value;
                }

                const jsonValue = JSON.stringify({
                    city: city,
                    state: state,
                    formatted: value
                });

                this.modelValue = jsonValue;

                if (this.$refs.hiddenInput) {
                    this.$refs.hiddenInput.value = jsonValue;
                    this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value || !this.$refs.cityInput) return;

            // Try to parse JSON first
            try {
                const json = JSON.parse(value);
                if (json.formatted) {
                    this.$refs.cityInput.value = json.formatted;
                    return;
                }
            } catch (e) {
                // Not JSON, use as-is
            }

            this.$refs.cityInput.value = value;
        },

        initAutocomplete() {
            const input = this.$refs.cityInput;
            if (!input) return;

            this.autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["(cities)"]
            });

            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();

                if (place.address_components) {
                    // A real selection wins over the pending manual blur commit.
                    this.placeSelected = true;

                    // Shared parser: country-aware city fallbacks (UK
                    // postal_town, sublocality wards, …).
                    const p = alvvaParsePlace(place);
                    const city = p.city, state = p.state;

                    const formatted = city && state ? `${city}, ${state}` : (city || state);

                    // Update the visible input
                    input.value = formatted;

                    // Store as JSON
                    const jsonValue = JSON.stringify({
                        city: city,
                        state: state,
                        formatted: formatted
                    });

                    this.modelValue = jsonValue;

                    // Update hidden input and trigger Livewire
                    if (this.$refs.hiddenInput) {
                        this.$refs.hiddenInput.value = jsonValue;
                        this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
                    }

                    // Close dropdown
                    input.blur();
                    setTimeout(() => input.focus(), 100);
                }
            });
        }
    }
}
